import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { primaryColor, secondaryColorThree, secondaryColorTwo, white } from "../config";
import Eyebrow from "../components/Eyebrow";

/* --------------------------------------------------
   THEME
-------------------------------------------------- */

const colors = {
  cream: "#f5f0e7",
  paper: "#faf8f3",
  ink: "#25231f",
  muted: "#746e65",
  brown: "#79543d",
  terracotta: "#a96e4d",
  sand: "#dfcdb6",
  dark: "#302d29",
  line: "rgba(37, 35, 31, 0.15)",
};

/* --------------------------------------------------
   SHARED COMPONENTS
-------------------------------------------------- */

interface EyebrowProps {
  children: React.ReactNode;
  dark?: boolean;
}

interface CTAButtonProps {
  children: React.ReactNode;
  href?: string;
  variant?: "dark" | "brown";
}

function CTAButton({ children, href = "#", variant = "dark" }: CTAButtonProps) {
  const styles =
    variant === "brown"
      ? {
        bg: colors.brown,
        color: "white",
        _hover: {
          bg: primaryColor,
        },
      }
      : {
        bg: primaryColor,
        color: colors.paper,
        _hover: {
          bg: secondaryColorThree,
        },
      };

  return (
    <Button
      as="a"
      href={href}
      borderRadius="0"
      px={7}
      py={6}
      h="auto"
      fontFamily="Arial, sans-serif"
      fontSize="12px"
      fontWeight="400"
      letterSpacing="0.12em"
      textTransform="uppercase"
      transition="all .25s ease"
      {...styles}
    >
      {children}
    </Button>
  );
}

/* --------------------------------------------------
   NAVIGATION
-------------------------------------------------- */

function Navigation() {
  return (
    <Flex
      as="nav"
      position="fixed"
      top="0"
      left="0"
      w="100%"
      zIndex={100}
      px={{ base: 5, md: "6vw" }}
      py={5}
      align="center"
      justify="space-between"
      bg="rgba(245,240,231,.88)"
      backdropFilter="blur(12px)"
      borderBottom="1px solid rgba(37,35,31,.05)"
    >
      <Text
        fontFamily="Arial, sans-serif"
        fontSize="18px"
        fontWeight="700"
        letterSpacing=".12em"
      >
        U2L
      </Text>

      <Flex
        display={{ base: "none", md: "flex" }}
        gap={8}
      >
        <Link
          href="#why"
          fontFamily="Arial, sans-serif"
          fontSize="12px"
          letterSpacing=".12em"
          color={colors.ink}
          _hover={{ color: colors.terracotta }}
        >
          WHY U2L
        </Link>

        <Link
          href="#communities"
          fontFamily="Arial, sans-serif"
          fontSize="12px"
          letterSpacing=".12em"
          color={colors.ink}
          _hover={{ color: colors.terracotta }}
        >
          COMMUNITIES
        </Link>

        <Link
          href="#join"
          fontFamily="Arial, sans-serif"
          fontSize="12px"
          letterSpacing=".12em"
          color={colors.ink}
          _hover={{ color: colors.terracotta }}
        >
          JOIN
        </Link>
      </Flex>
    </Flex>
  );
}

/* --------------------------------------------------
   HERO
-------------------------------------------------- */

function Hero() {
  return (
    <Box
      id={"start"}
      as="section"
      minH="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      px={6}
      pt={{ base: "2rem", md: "4rem" }}
      bg={colors.cream}
      color={primaryColor}
    >
      <Box maxW="950px" >
        <Eyebrow>
          A movement by SwaDharma Spandana
        </Eyebrow>

        <Heading
          fontFamily="Arial, sans-serif"
          fontSize={{
            base: "90px",
            sm: "120px",
            md: "17vw",
            lg: "210px",
          }}
          fontWeight="700"
          lineHeight=".78"
          letterSpacing="-0.09em"
          ml="-0.08em"
        >
          U2L
        </Heading>

        <Box as="strong" color={secondaryColorThree}>
          नेति चरैवेति - Not this; keep moving forward!
        </Box>

        <Heading
          as="h2"
          mt={12}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "25px", md: "38px" }}
          fontWeight="400"
          letterSpacing=".02em"
          color={secondaryColorThree}
        >
          Unlearning · Living · Learning
        </Heading>

        <Text
          maxW="700px"
          mx="auto"
          mt={9}
          fontSize={{ base: "17px", md: "20px" }}
          color={primaryColor}
        >
          A movement exploring how we can create spaces
          where people learn to live fully, learn freely,
          and grow beyond borrowed expectations.
        </Text>

        <Text
          mt={9}
          fontFamily="Arial, sans-serif"
          fontSize="12px"
          letterSpacing=".18em"
          textTransform="uppercase"
          color={secondaryColorThree}
        >
          The Movement of U2L
        </Text>

        <Box mt={14}>
          <CTAButton href="#why">
            Explore the movement
          </CTAButton>
        </Box>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------
   CENTRAL QUESTION
-------------------------------------------------- */

function CentralQuestion() {
  return (
    <Box
      mt="2rem"
      as="section"
      id="why"
      minH="75vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      px={6}
      bg={white}
      color={colors.paper}
    >
      <Box maxW="1050px">
        <Heading
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "40px", md: "5vw", lg: "70px" }}
          fontWeight="400"
          lineHeight="1.15"
          color={primaryColor}
        >
          How can our children be
          <br />
          equipped for the future?
        </Heading>

        <Text
          mt={10}
          color={secondaryColorThree}
          fontFamily="Arial, sans-serif"
          fontSize="13px"
          letterSpacing=".1em"
          textTransform="uppercase"
        >
          Perhaps this is where we begin.
        </Text>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------
   INTRO
-------------------------------------------------- */

function Intro() {
  return (
    <Box
      as="section"
      textAlign="center"
      px={{ base: 6, md: "8vw" }}
      py={{ base: 20, md: 30 }}
      color={primaryColor}
    >
      <Container maxW="1100px">
        <Eyebrow>Why U2L</Eyebrow>

        <Heading
          maxW="900px"
          mx="auto"
          fontSize={{ base: "38px", md: "5vw", lg: "62px" }}
          fontWeight="400"
          lineHeight="1.15"

        >
          What if being equipped for the future
          means more than being prepared for it?
        </Heading>

        <VStack
          maxW="720px"
          mx="auto"
          mt={9}
          spacing={6}
          color={secondaryColorThree}
          fontSize={{ base: "17px", md: "19px" }}
        >
          <Text>
            We want our children to be able to live fully
            even in our absence, not merely fulfil
            our expectations of them.
            <br />

            To make choices. To meet uncertainty.
            To relate. To create. To question.
            <br />
            <Box as="strong" textTransform="uppercase" m={0} color={primaryColor}>
              To keep learning!
            </Box>
          </Text>


          <Text>
            U2L begins with this simple concern
            and opens it into a larger inquiry:
            <br />
            <Box as="strong" color={primaryColor}>
              how do we create spaces where human
              beings can learn to live?
            </Box>
          </Text>
        </VStack>
      </Container>
    </Box>
  );
}

/* --------------------------------------------------
   U2L CARDS
-------------------------------------------------- */

const u2lItems = [
  {
    letter: "U",
    title: "Unlearning",
    text:
      "Looking at what we have inherited, assumed and been conditioned to believe, and becoming capable of questioning it.",
  },
  {
    letter: "2",
    title: "Living",
    text:
      "Meeting life directly, through experience, relationship, play, work, nature, failure, uncertainty and wonder.",
  },
  {
    letter: "L",
    title: "Learning",
    text:
      "Rediscovering learning as something alive: observing, questioning, experimenting, creating and discovering.",
  },
];

function U2LSection() {
  return (
    <Box
      display={"flex"}
      flexDir={"column"}
      bg={colors.paper}
      px={{ base: 6, md: "8vw" }}
      py={{ base: 20, md: 30 }}
    >
      <Container maxW="1100px">
        <Eyebrow>The three movements</Eyebrow>

        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(3, 1fr)",
          }}
          gap="1px"
          bg={colors.line}
          mt={16}
        >
          {u2lItems.map((item) => (
            <Box
              key={item.letter}
              bg={colors.paper}
              p={{ base: 8, md: 12 }}
              minH="330px"
            >
              <Text
                fontFamily="Arial, sans-serif"
                fontSize="60px"
                fontWeight="700"
                lineHeight="1"
                color={primaryColor}
              >
                {item.letter}
              </Text>

              <Heading
                mt={6}
                fontSize="32px"
                fontWeight="400"
                color={secondaryColorThree}
              >
                {item.title}
              </Heading>

              <Text
                mt={4}
                color={primaryColor}
                fontSize="16px"
              >
                {item.text}
              </Text>
            </Box>
          ))}
        </Grid>
      </Container>
      <Box as="strong" color={secondaryColorThree} textAlign={"center"}>
        नेति चरैवेति - Not this; keep moving forward!
      </Box>
    </Box>
  );
}

/* --------------------------------------------------
   COMMUNITY CARDS
-------------------------------------------------- */

const communities = [
  {
    number: "01 · PARENT COMMUNITY",
    title: "Parents",
    question: "How can our children be equipped for the future?",
    description:
      "A conversation for parents who genuinely want their children to be able to live fully in their absence, not merely fulfil parental expectations.",
    activity: "A conversation",
    bg: "#e8dccb",
  },
  {
    number: "02 · CHILDREN COMMUNITY",
    title: "Children",
    question: "What do you see?",
    description:
      "We begin simply: with a story. No predetermined lesson. No expected answer. A space to listen, imagine, question, sing, play and explore.",
    activity: "A story session",
    bg: "#d9c3aa",
  },
  {
    number: "03 · VOLUNTEER COMMUNITY",
    title: "Volunteers",
    question: "What are you willing to do?",
    description:
      "People who want to create such spaces in the world, not merely discuss the idea, but facilitate, participate, contribute, learn and grow through action.",
    activity: "Facilitate & act",
    bg: "#cec8bd",
  },
];

interface CommunityCardProps {
  number: string;
  title: string;
  question: string;
  description: string;
  activity: string;
  bg: string;
}

function CommunityCard({
  number,
  title,
  question,
  description,
  activity,
  bg,
}: CommunityCardProps) {
  return (
    <Flex
      direction="column"
      justify="space-between"
      minH={{ base: "450px", md: "520px" }}
      p={{ base: 8, md: 11 }}
      bg={bg}
    >
      <Box>
        <Text
          fontFamily="Arial, sans-serif"
          fontSize="11px"
          letterSpacing=".18em"
          color={secondaryColorThree}
        >
          {number}
        </Text>

        <Heading
          mt={9}
          fontSize={{ base: "40px", md: "44px" }}
          fontWeight="400"
          lineHeight="1"
          color={primaryColor}
        >
          {title}
        </Heading>

        <Text
          mt={9}
          fontSize="23px"
          lineHeight="1.35"
          color={secondaryColorThree}
        >
          {question}
        </Text>

        <Text
          mt={6}
          fontSize="16px"
          color={primaryColor}
        >
          {description}
        </Text>
      </Box>

      <Text
        mt={9}
        pt={5}
        borderTop="1px solid rgba(37,35,31,.15)"
        fontFamily="Arial, sans-serif"
        fontSize="11px"
        letterSpacing=".1em"
        textTransform="uppercase"
        color={secondaryColorThree}
      >
        {activity}
      </Text>
    </Flex>
  );
}

function Communities() {
  return (
    <Box
      as="section"
      id="communities"
      px={{ base: 6, md: "8vw" }}
      py={{ base: 20, md: 30 }}
    >
      <Container maxW="1100px">
        <Box textAlign="center" maxW="800px" mx="auto">
          <Eyebrow>Three communities</Eyebrow>

          <Heading
            fontSize={{ base: "40px", md: "5vw", lg: "65px" }}
            fontWeight="400"
            lineHeight="1.1"
            color={primaryColor}
          >
            Different people.
            <br />
            One movement.
          </Heading>

          <Text
            mt={6}
            color={secondaryColorThree}
            fontSize="18px"
          >
            U2L begins by bringing together parents,
            children and people willing to create
            such spaces in the world.
          </Text>
        </Box>

        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          gap={5}
          mt={16}
        >
          {communities.map((community) => (
            <CommunityCard
              key={community.number}
              {...community}
            />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

/* --------------------------------------------------
   PARENT DETAIL
-------------------------------------------------- */

function ParentSection() {
  return (
    <Box
      as="section"
      px={6}
      py={{ base: 20, md: 30 }}
      textAlign="center"
    >
      <Box maxW="900px" mx="auto">
        <Eyebrow>For parents</Eyebrow>

        <Heading
          fontSize={{ base: "38px", md: "5vw", lg: "60px" }}
          fontWeight="400"
          lineHeight="1.15"
          color={primaryColor}
        >
          Your child will one day
          <br />
          have to live independently.
        </Heading>

        <Text
          mt={10}
          fontSize={{ base: "22px", md: "25px" }}
          color={secondaryColorThree}
        >
          What would you want them
          to carry with them?
        </Text>

        <Text
          mt={6}
          color={secondaryColorThree}
          fontSize="18px"
        >
          Not simply what they know. <br />
          Not simply what they can achieve.
          <br />
          But what they are capable of perceiving & creating
          <br />
        </Text>
        <Box as="strong" color={primaryColor} textTransform="uppercase">
          independently
        </Box>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------
   CHILDREN DETAIL
-------------------------------------------------- */

function ChildrenSection() {
  return (
    <Box
      as="section"
      bg={colors.paper}
      px={{ base: 6, md: "8vw" }}
      py={{ base: 20, md: 30 }}
    >
      <Grid
        maxW="1100px"
        mx="auto"
        templateColumns={{
          base: "1fr",
          md: "1fr 1fr",
        }}
        gap={{ base: 10, md: 20 }}
        alignItems="center"
      >
        <Box>
          <Eyebrow>For children</Eyebrow>

          <Heading
            fontSize={{ base: "40px", md: "5vw", lg: "65px" }}
            fontWeight="400"
            lineHeight="1.05"
            color={primaryColor}
          >
            A story is enough
            <br />
            to begin.
          </Heading>

          <Box color={secondaryColorThree}>


            <Text
              mt={8}
              fontSize="18px"
            >
              We don't begin by telling children
              what they should learn from a story.
            </Text>

            <Text
              mt={5}
              fontSize="18px"
            >
              We give them the space to explore it,
              to notice, question, imagine,
              disagree, create and wonder.
            </Text>
          </Box>
        </Box>

        <Flex
          minH="400px"
          p={{ base: 10, md: 14 }}
          bg={white}
          align="center"
          justify="center"
          direction="column"
          textAlign="center"
        >
          <Text
            fontSize={{ base: "30px", md: "36px" }}
            lineHeight="1.2"
            color={primaryColor}
          >
            “What did you see
            <br />
            in the story?”
          </Text>

          <Text
            mt={9}
            fontFamily="Arial, sans-serif"
            color={secondaryColorThree}
            fontSize="11px"
            letterSpacing=".15em"
            textTransform="uppercase"
          >
            No right answer required
          </Text>
        </Flex>
      </Grid>
    </Box>
  );
}

/* --------------------------------------------------
   VOLUNTEER DETAIL
-------------------------------------------------- */

function VolunteerSection() {
  return (
    <Box
      as="section"
      bg={colors.sand}
      color={primaryColor}
      px={6}
      py={{ base: 20, md: 30 }}
      textAlign="center"
    >
      <Box maxW="950px" mx="auto">
        <Eyebrow>For volunteers</Eyebrow>

        <Heading
          fontSize={{ base: "40px", md: "5vw", lg: "65px" }}
          fontWeight="400"
          lineHeight="1.1"
        >
          Don&apos;t just Discuss;
          <br />
          DO!
        </Heading>

        <Text
          mt={12}
          fontSize={{ base: "22px", md: "27px" }}
          color={secondaryColorTwo}
        >
          The deepest questions may not need
          philosophical answers.
        </Text>

        <VStack
          mt={8}
          spacing={5}
          color={secondaryColorThree}
          fontSize="17px"
        >
          <Text>They can become actions.</Text>

          <Text>
            Facilitate a story. Sit with a child.
            Create a space. Try. Fail. Observe.
            Reflect. Try again.
          </Text>

          <Text maxW="700px">
            U2L invites people to explore
            their own questions by participating
            in the work itself, while contributing
            to a more sustainable future.
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------
   CORE PRINCIPLE
-------------------------------------------------- */

function PrincipleSection() {
  return (
    <Box
      as="section"
      minH="75vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      px={6}
      bg={colors.cream}
    >
      <Box>
        {/* <Eyebrow></Eyebrow> */}
        <Box color={secondaryColorThree}>
          The spirit of U2L
          <br />
          नेति चरैवेति - Not this; keep moving forward!
        </Box>

        <Heading
          maxW="1000px"
          fontSize={{ base: "38px", md: "5vw", lg: "70px" }}
          fontWeight="400"
          lineHeight="1.15"
          color={primaryColor}
        >
          We don&apos;t want to prepare people
          merely{" "}
          <Box as="em" color={secondaryColorThree}>
            for life.
          </Box>

          <br />
          <br />

          We want to create spaces where people
          can{" "}
          <Box as="em" color={secondaryColorThree}>
            learn through life,
            <br />
            while living it.
          </Box>
        </Heading>
      </Box>
    </Box>
  );
}

/* --------------------------------------------------
   CTA
-------------------------------------------------- */

function JoinSection() {
  return (
    <Box
      as="section"
      id="join"
      bg={colors.paper}
      textAlign="center"
      px={6}
      py={{ base: 20, md: 30 }}
    >
      <Container maxW="1100px">
        <Eyebrow>Begin with us</Eyebrow>

        <Heading
          fontSize={{ base: "40px", md: "5vw", lg: "65px" }}
          fontWeight="400"
          color={primaryColor}
        >
          Perhaps the first step
          <br />
          is simply a question.
        </Heading>

        <Text
          maxW="650px"
          mx="auto"
          mt={6}
          color={secondaryColorThree}
          fontSize="18px"
        >
          U2L is beginning with conversations,
          stories and action, bringing together
          people who want to explore a different
          possibility for how we live and learn.
        </Text>

        <Box mt={12}>
          <CTAButton
            href="#contactUs"
          >
            <Box>
              Know More! <br />
              Contact us
            </Box>
          </CTAButton>
        </Box>
      </Container>
    </Box>
  );
}

/* --------------------------------------------------
   FOOTER
-------------------------------------------------- */

function Footer() {
  return (
    <Flex
      as="footer"
      px={{ base: 6, md: "8vw" }}
      py={16}
      bg={colors.ink}
      color={colors.paper}
      justify="space-between"
      align={{ base: "flex-start", md: "flex-end" }}
      direction={{ base: "column", md: "row" }}
      gap={10}
    >
      <Box>
        <Heading
          fontSize="27px"
          fontWeight="400"
        >
          The Movement of U2L
        </Heading>

        <Text
          mt={2}
          color="#aaa39a"
          fontFamily="Arial, sans-serif"
          fontSize="11px"
          letterSpacing=".08em"
        >
          by SwaDharma Spandana
        </Text>
      </Box>

      <Text
        color="#aaa39a"
        fontFamily="Arial, sans-serif"
        fontSize="11px"
        letterSpacing=".08em"
      >
        Unlearning · Living · Learning
      </Text>
    </Flex>
  );
}

/* --------------------------------------------------
   MAIN PAGE
-------------------------------------------------- */

export default function U2LProto() {
  return (
    <Box
      fontFamily="Georgia, 'Times New Roman', serif"
      lineHeight="1.65"

      sx={{
        scrollBehavior: "smooth",

        "& *": {
          boxSizing: "border-box",
        },
      }}
    >


      <main>
        <Hero />
        <CentralQuestion />
        <Intro />
        <U2LSection />
        <br />
        <br />
        <Communities />
        <ParentSection />
        <ChildrenSection />
        <VolunteerSection />
        <PrincipleSection />
        <JoinSection />
      </main>

      {/* <Footer /> */}
    </Box>
  );
}
